import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { LCLShipmentModel, QuoteModel } from '@data-layer/models/lcl';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EnumHintKey } from '@data-layer/enums/common';
import { enumShippingRole } from '@data-layer/enums/user';
import { Router } from '@angular/router';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { mergeMap, pairwise, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { EntityModel } from '@data-layer/models/entities';
import { LclQuoteService } from '@app/modules/lcl-shipping/services';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { GenericService } from '@services/generic.service';

@Component({
  selector: 'lcl-edit-incoterms',
  templateUrl: './lcl-edit-incoterms.component.html',
  styleUrls: ['./lcl-edit-incoterms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclEditIncotermsComponent implements OnInit, OnDestroy {
  @Input() public shipment: LCLShipmentModel;
  @Input() public isHideRoleSelector: boolean;
  @Input() public isManagement: boolean;
  @Output() public dataSaved: EventEmitter<{
    exportLicenseRequested: boolean;
    incoTermsType: enumIncotermsType;
    shippingRole: enumShippingRole;
  }> = new EventEmitter<{
    exportLicenseRequested: boolean;
    incoTermsType: enumIncotermsType;
    shippingRole: enumShippingRole;
  }>();
  public formGroup: UntypedFormGroup;
  public incotermsControl: UntypedFormControl;
  public exportLicenseRequestedControl: UntypedFormControl;
  public shippingRoleControl: UntypedFormControl;
  public shippingRoleSwitchControl: UntypedFormControl;
  public enumIncotermsType: typeof enumIncotermsType = enumIncotermsType;
  public enumHintKey: typeof EnumHintKey = EnumHintKey;
  public currentShippingRole: enumShippingRole = enumShippingRole.OTHER;
  public isChangeFOBToFCA: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private lclQuoteService: LclQuoteService,
    private genericService: GenericService,
  ) {}

  public ngOnInit(): void {
    this.createForm();
    this.incotermsControl = this.formGroup.get('incoTermsType') as UntypedFormControl;
    this.exportLicenseRequestedControl = this.formGroup.get('exportLicenseRequested') as UntypedFormControl;
    this.shippingRoleControl = this.formGroup.get('shippingRole') as UntypedFormControl;
    this.shippingRoleSwitchControl = new UntypedFormControl(enumShippingRole.SHIPPER === this.shipment?.shippingRole);

    this.incotermsControl.valueChanges
      .pipe(startWith(this.incotermsControl.value), pairwise(), takeUntil(this.destroy$))
      .subscribe(([prevVal, currVal]: [enumIncotermsType, enumIncotermsType]) => {
        this.isChangeFOBToFCA = prevVal === enumIncotermsType.FOB && currVal === enumIncotermsType.FCA;
        if (currVal === enumIncotermsType.FOB) {
          this.exportLicenseRequestedControl.setValue(false, { emitEvent: false });
        }

        setTimeout(() => this.genericService.recalcHints$.next());
      });

    this.currentShippingRole = this.shipment?.shippingRole;

    this.shippingRoleSwitchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateShippingRole();
    });

    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public save(): void {
    const quoteUrls: string[] = ['edit', 'booking'];
    const managementUrls: string[] = ['shipments-management'];

    if ([...quoteUrls, ...managementUrls].some((item: string) => this.router.url.includes(item))) {
      this.updateQuoteData(this.shipment as unknown as QuoteModel)
        .pipe(take(1))
        .subscribe((quote: QuoteModel) => {
          const oldEntityIds: number[] = [this.shipment?.consignee?.id, this.shipment?.shipper?.id];
          const newEntityIds: number[] = [quote?.consignee?.id, quote?.shipper?.id];
          const result: number[] = [];

          oldEntityIds.forEach((item: number) => {
            if (!!item && !newEntityIds.includes(item)) {
              result.push(item);
            }
          });

          this.lclQuoteService.setEntitiesToClean(result);

          this.dataSaved.emit({ ...quote, ...this.formGroup.value });
        });
    } else {
      this.dataSaved.emit({
        ...this.shipment,
        consigneeId: this.shipment?.consignee?.id,
        notifyPartyId: this.shipment?.notifyParty?.id,
        shipperId: this.shipment?.shipper?.id,
        ...this.formGroup.value,
      });
    }
  }

  public updateShippingRole(): void {
    const newRole: enumShippingRole =
      this.currentShippingRole === enumShippingRole.IMPORTER ? enumShippingRole.SHIPPER : enumShippingRole.IMPORTER;
    let newIncotermsValue: enumIncotermsType = this.shipment.incoTermsType;

    switch (this.incotermsControl.value) {
      case enumIncotermsType.FCA:
        newIncotermsValue = enumIncotermsType.DAT;
        break;
      case enumIncotermsType.FOB:
        newIncotermsValue = enumIncotermsType.CPT_CFR;
        break;
      case enumIncotermsType.DAT:
        newIncotermsValue = enumIncotermsType.FCA;
        break;
      case enumIncotermsType.CPT_CFR:
        newIncotermsValue = enumIncotermsType.FOB;
        break;
    }

    this.currentShippingRole = newRole;
    this.shippingRoleControl.setValue(newRole);
    this.incotermsControl.setValue(newIncotermsValue);
  }

  public get incotermsHeaderLabel(): string {
    switch (this.formGroup?.value?.incoTermsType) {
      case enumIncotermsType.FCA:
        return 'LABELS.FCA_LABEL';
      case enumIncotermsType.CPT_CFR:
        return 'LABELS.CPT_CFR_LABEL';
      case enumIncotermsType.DAT:
        return 'LABELS.DAT_LABEL';
      case enumIncotermsType.FOB:
        return 'LABELS.FOB_LABEL';
      default:
        return '';
    }
  }

  public get incotermsHeaderHint(): EnumHintKey {
    switch (this.formGroup?.value?.incoTermsType) {
      case enumIncotermsType.FCA:
        return EnumHintKey.fcaHint;
      case enumIncotermsType.CPT_CFR:
        return EnumHintKey.cptCfrHint;
      case enumIncotermsType.DAT:
        return EnumHintKey.datHint;
      case enumIncotermsType.FOB:
        return EnumHintKey.fobHint;
      default:
        return;
    }
  }

  public get isLeftSwitchActive(): boolean {
    return [enumShippingRole.IMPORTER, true].includes(this.currentShippingRole);
  }

  public get isRightSwitchActive(): boolean {
    return [enumShippingRole.SHIPPER, false].includes(this.currentShippingRole);
  }

  public setIncoterms(state: number): void {
    this.formGroup.get('incoTermsType').setValue(state, { emitEvent: false });
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      shippingRole: [this.shipment?.shippingRole],
      incoTermsType: [!!this.shipment.incoTermsType ? this.shipment.incoTermsType : true, [Validators.required]],
      exportLicenseRequested: [!!this.shipment.exportLicenseRequested ? this.shipment.exportLicenseRequested : false],
    });
  }

  private updateQuoteData(oldData: QuoteModel): Observable<QuoteModel> {
    const entity: EntityModel =
      this.currentShippingRole === enumShippingRole.SHIPPER
        ? { ...oldData.consignee, entityCode: enumEntityModelCode.SHIPPER }
        : { ...oldData.shipper, entityCode: enumEntityModelCode.CONSIGNEE };

    if (oldData.shippingRole === this.currentShippingRole) {
      return of({
        ...oldData,
        consigneeId: oldData?.consignee?.id,
        notifyPartyId: oldData?.notifyParty?.id,
        shipperId: oldData?.shipper?.id,
      });
    }

    return of(this.lclQuoteService.handleEntities({ ...oldData, ...this.formGroup.value }, entity, true)).pipe(
      take(1),
      mergeMap((entities: { consignee: Observable<EntityModel>; notifyParty: Observable<EntityModel>; shipper: Observable<EntityModel> }) =>
        combineLatest([entities.consignee, entities.notifyParty, entities.shipper]),
      ),
      switchMap(([consignee, notifyParty, shipper]: [EntityModel, EntityModel, EntityModel]) => {
        return of({
          ...oldData,
          consignee,
          consigneeId: consignee?.id,
          notifyParty,
          notifyPartyId: notifyParty?.id,
          shipper,
          shipperId: shipper?.id,
        });
      }),
    );
  }
}
